import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { DatePipe } from '@angular/common';
import { PhoneMaskPipe } from '@pipes/phoneMask.pipe';

declare let _chatlio;

export type ChatlioCustomData = { [key: string]: string | number };

const CHATLIO_WIDGET_ID = 'f38e0fe3-2c48-4010-4e66-b2360496e878';
const CHATLIO_SCRIPT_ID = 'chatlio-widget-embed';

@Injectable({
  providedIn: 'root',
})
export class ChatlioService {
  readonly isEnabled = import.meta.env.NG_APP_PRODUCTION === 'true';

  constructor(
    private readonly authService: AuthService,
    private readonly datePipe: DatePipe,
    private readonly phoneMaskPipe: PhoneMaskPipe
  ) {}

  init(id?: number, customData?: ChatlioCustomData): void {
    if (!this.isEnabled) {
      return;
    }

    this.ensureLoaded();
    if (!this.isReady()) {
      return;
    }

    if (customData) {
      _chatlio.identify(id, customData);
      return;
    }
    if (!id) {
      const currentUser = this.authService.currentUser;
      if (!currentUser) return;
      _chatlio.identify(id, {
        name: `${currentUser.first_name} ${currentUser.last_name}`,
        email: currentUser.email,
        phone: currentUser.mobile_phone ? this.phoneMaskPipe.transform(currentUser.mobile_phone) : '',
        company: currentUser.account.company?.name || '',
        account: currentUser.account.number,
        agent: currentUser.account.agent ? ` / ${currentUser.account.agent.first_name} ${currentUser.account.agent.last_name}` : '',
        account_id: currentUser.account.id,
        'Created at': `${this.datePipe.transform(currentUser.account.created_at, 'shortDate')} by ${
          currentUser.account.created_by_user?.first_name
        } ${currentUser.account.created_by_user?.last_name}`,
        'Rides (Done/Booked)': `${currentUser._doneCount}/${currentUser._pendingCount}`,
      });
    }

    setTimeout(() => {
      if (!this.isReady()) {
        return;
      }
      const closeElement = document.querySelector('.chatlio-chat-header__minimize-button');
      if (closeElement) {
        closeElement.addEventListener('click', () => {
          if (this.isReady()) {
            _chatlio.hide();
          }
        });
      }
    }, 5000);
  }

  close(): void {
    if (!this.isEnabled || !this.isReady()) {
      return;
    }
    if (_chatlio.reset) _chatlio.reset();
    _chatlio.hide();
  }

  open(): void {
    if (!this.isEnabled) {
      return;
    }
    this.ensureLoaded();
    if (!this.isReady()) {
      return;
    }
    _chatlio.open();
  }

  private isReady(): boolean {
    return typeof _chatlio !== 'undefined';
  }

  private ensureLoaded(): void {
    if (!this.isEnabled || document.getElementById(CHATLIO_SCRIPT_ID)) {
      return;
    }

    (window as any)._chatlio = (window as any)._chatlio || [];
    const chatlio = (window as any)._chatlio;

    const methodNames = ['configure', 'identify', 'track', 'show', 'hide', 'isShown', 'isOnline', 'page', 'open', 'showOrHide'];
    for (const methodName of methodNames) {
      if (!chatlio[methodName]) {
        chatlio[methodName] = (...args: unknown[]) => {
          chatlio.push([methodName, ...args]);
        };
      }
    }

    const script = document.createElement('script');
    const firstScript = document.getElementsByTagName('script')[0];
    script.id = CHATLIO_SCRIPT_ID;
    script.src = 'https://w.chatlio.com/w.chatlio-widget.js';
    script.async = true;
    script.setAttribute('data-embed-version', '2.3');
    script.setAttribute('data-widget-options', '{"embedSidebar": true}');
    script.setAttribute('data-widget-id', CHATLIO_WIDGET_ID);
    firstScript.parentNode.insertBefore(script, firstScript);
  }
}
